<script setup>
import { computed, inject, onMounted, ref, toRefs } from 'vue';
import { useRouter } from 'vue-router';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { statusStyle } from './ticketStatusStyles';
import { filters } from './portalTicketFilters';

const config = inject('portalTicketsConfig');
const labels = config.listLabels;
const ticketsPath = config.ticketsPath;
const accent = config.accent;
const canViewCompanyTickets = config.canViewCompanyTickets;
const router = useRouter();

const STATUS_KEYS = ['open', 'resolved', 'all'];
const PERIOD_KEYS = ['today', '30d', '90d', 'all'];
const VIEW_KEYS = ['mine', 'company'];

const { status, period, view, query, page } = toRefs(filters);
const loading = ref(false);
const hasError = ref(false);
const conversations = ref([]);
const counts = ref({});
const pagination = ref({});

let searchTimer = null;
let requestId = 0;

const showAssignee = computed(() => status.value === 'resolved');
const showRequester = computed(() => view.value === 'company');

const columnTemplate = computed(() => {
  const columns = ['70px', 'minmax(200px,1fr)'];
  if (showRequester.value) columns.push('150px');
  columns.push('150px');
  if (showAssignee.value) columns.push('160px');
  columns.push('110px');
  return columns.join(' ');
});

const countLabel = computed(() =>
  (labels.count_label || '')
    .replace('%{shown}', conversations.value.length)
    .replace(
      '%{total}',
      pagination.value.total_count ?? conversations.value.length
    )
);

const fetchTickets = async () => {
  requestId += 1;
  const currentRequest = requestId;
  loading.value = true;
  hasError.value = false;
  const params = new URLSearchParams();
  if (status.value !== 'all') params.set('status', status.value);
  if (period.value !== '30d') params.set('period', period.value);
  if (view.value === 'company') params.set('view', 'company');
  if (query.value) params.set('q', query.value);
  if (page.value > 1) params.set('page', page.value);

  const queryString = params.toString();
  const url = `${ticketsPath}.json${queryString ? `?${queryString}` : ''}`;
  try {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to load tickets');
    const data = await response.json();

    if (currentRequest !== requestId) return;
    conversations.value = data.conversations;
    counts.value = data.counts;
    pagination.value = data.pagination;
  } catch {
    if (currentRequest !== requestId) return;
    hasError.value = true;
  } finally {
    if (currentRequest === requestId) loading.value = false;
  }
};

const resetAndFetch = () => {
  page.value = 1;
  fetchTickets();
};

const selectStatus = value => {
  if (status.value === value) return;
  status.value = value;
  resetAndFetch();
};

const selectPeriod = value => {
  if (period.value === value) return;
  period.value = value;
  resetAndFetch();
};

const selectView = value => {
  if (view.value === value) return;
  view.value = value;
  resetAndFetch();
};

const onSearch = () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(resetAndFetch, 400);
};

const goToPage = value => {
  page.value = value;
  fetchTickets();
};

const openTicket = conversation => {
  if (conversation.chattable && window.$chatwoot) {
    window.$chatwoot.setConversation(conversation.display_id);
    return;
  }
  router.push({
    name: 'ticket-detail',
    params: { displayId: conversation.display_id },
  });
};

onMounted(fetchTickets);
</script>

<template>
  <div class="mx-auto w-full max-w-[1240px]">
    <div
      class="rounded-[20px] border border-solid border-[#E4E6EB] bg-white p-7 shadow-[0_1px_3px_rgba(16,20,28,0.05)] dark:border-slate-800 dark:bg-slate-900"
    >
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1
            class="mb-1 mt-[7px] text-[27px] font-extrabold tracking-[-0.6px] text-[#1B1F27] dark:text-white"
          >
            {{ labels.title }}
          </h1>
          <p class="text-[13.5px] text-[#6B7280]">{{ labels.subtitle }}</p>
        </div>

        <div
          v-if="canViewCompanyTickets"
          class="inline-flex gap-[3px] rounded-[10px] border border-solid border-[#E4E6EB] bg-[#F3F4F7] p-[3px] dark:border-slate-700 dark:bg-slate-800/60"
        >
          <button
            v-for="viewKey in VIEW_KEYS"
            :key="viewKey"
            type="button"
            class="rounded-lg px-[13px] py-1.5 text-[12.5px] font-semibold transition-colors"
            :class="
              view === viewKey
                ? 'bg-[#1B1F27] text-white dark:bg-white dark:text-slate-900'
                : 'text-[#6B7280] hover:text-[#1B1F27] dark:hover:text-white'
            "
            @click="selectView(viewKey)"
          >
            {{ labels.views[viewKey] }}
          </button>
        </div>
      </div>

      <div
        class="mt-[26px] flex flex-wrap items-center gap-[18px] border-b border-solid border-[#E7E9ED] dark:border-slate-800"
      >
        <div class="flex max-w-full overflow-x-auto">
          <button
            v-for="statusKey in STATUS_KEYS"
            :key="statusKey"
            type="button"
            class="mr-3 inline-flex shrink-0 items-center gap-[7px] border-b-[2.5px] border-solid pb-[13px] pt-[10px] text-[14px] transition-colors"
            :class="
              status === statusKey
                ? 'font-bold text-[#1B1F27] dark:text-white'
                : 'border-transparent font-medium text-[#8A91A0] hover:text-[#1B1F27] dark:hover:text-white'
            "
            :style="{
              borderBottomColor: status === statusKey ? accent : 'transparent',
            }"
            @click="selectStatus(statusKey)"
          >
            {{ labels.tabs[statusKey] }}
            <span
              class="rounded-full px-[7px] py-px text-[11px] font-bold tabular-nums"
              :class="
                status === statusKey
                  ? ''
                  : 'bg-[#EFF0F3] text-[#9AA1AC] dark:bg-slate-800 dark:text-slate-400'
              "
              :style="
                status === statusKey
                  ? { color: accent, backgroundColor: `${accent}1F` }
                  : {}
              "
            >
              {{ counts[statusKey] ?? 0 }}
            </span>
          </button>
        </div>

        <div class="flex-1" />

        <div
          class="flex w-full flex-wrap items-center gap-2.5 pb-2.5 sm:w-auto"
        >
          <div
            class="flex w-full min-w-0 flex-1 items-center gap-2 rounded-[10px] border border-solid border-[#E4E6EB] bg-[#F3F4F7] px-3 py-2 sm:w-[230px] sm:flex-none dark:border-slate-700 dark:bg-slate-800/60"
          >
            <span class="i-lucide-search size-[15px] shrink-0 text-[#9AA1AC]" />
            <input
              v-model="query"
              type="text"
              :placeholder="labels.search_placeholder"
              class="w-full border-0 bg-transparent p-0 text-[13px] text-[#1B1F27] outline-none placeholder:text-[#9AA1AC] focus:ring-0 dark:text-slate-200"
              @input="onSearch"
            />
          </div>

          <div
            class="flex max-w-full gap-[3px] overflow-x-auto rounded-[10px] border border-solid border-[#E4E6EB] bg-[#F3F4F7] p-[3px] dark:border-slate-700 dark:bg-slate-800/60"
          >
            <button
              v-for="periodKey in PERIOD_KEYS"
              :key="periodKey"
              type="button"
              class="shrink-0 whitespace-nowrap rounded-lg px-[13px] py-1.5 text-[12.5px] font-semibold transition-colors"
              :class="
                period === periodKey
                  ? 'bg-[#1B1F27] text-white dark:bg-white dark:text-slate-900'
                  : 'text-[#6B7280] hover:text-[#1B1F27] dark:hover:text-white'
              "
              @click="selectPeriod(periodKey)"
            >
              {{ labels.periods[periodKey] }}
            </button>
          </div>
        </div>
      </div>

      <div :class="{ 'pointer-events-none opacity-60': loading }">
        <div
          v-if="hasError"
          class="mt-[18px] flex flex-col items-center justify-center rounded-[14px] border border-dashed border-[#f0d4d2] px-6 py-16 text-center dark:border-slate-800"
        >
          <div
            class="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fdeceb] text-[#e5484d] dark:bg-slate-800"
          >
            <span class="i-lucide-alert-circle size-6" />
          </div>
          <p class="text-base font-bold text-[#1B1F27] dark:text-slate-100">
            {{ labels.error.title }}
          </p>
          <p class="mt-1 text-sm text-[#6B7280]">{{ labels.error.subtitle }}</p>
          <button
            type="button"
            class="mt-4 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            :style="{ backgroundColor: accent }"
            @click="fetchTickets"
          >
            <span class="i-lucide-rotate-cw size-4" />
            {{ labels.error.retry }}
          </button>
        </div>

        <template v-else>
          <div
            v-if="conversations.length"
            class="mt-[18px] hidden overflow-hidden rounded-[14px] border border-solid border-[#E4E6EB] bg-white shadow-[0_1px_2px_rgba(16,20,28,0.03)] dark:border-slate-800 dark:bg-slate-900 md:block"
          >
            <div class="overflow-x-auto">
              <div class="min-w-[760px]">
                <div
                  class="grid items-center gap-[14px] bg-[#FBFBFC] px-[22px] py-[13px] text-[11px] font-bold uppercase tracking-[0.6px] text-[#9AA1AC] dark:bg-slate-800/40"
                  :style="{ gridTemplateColumns: columnTemplate }"
                >
                  <div>{{ labels.columns.id }}</div>
                  <div>{{ labels.columns.subject }}</div>
                  <div v-if="showRequester">{{ labels.columns.requester }}</div>
                  <div>{{ labels.columns.status }}</div>
                  <div v-if="showAssignee">{{ labels.columns.assignee }}</div>
                  <div>{{ labels.columns.updated }}</div>
                </div>

                <div
                  v-for="conversation in conversations"
                  :key="conversation.display_id"
                  class="grid cursor-pointer items-center gap-[14px] border-t border-solid border-[#F0F1F4] px-[22px] py-3.5 transition-colors hover:bg-[#FAFAFB] dark:border-slate-800 dark:hover:bg-slate-800/40"
                  :style="{ gridTemplateColumns: columnTemplate }"
                  @click="openTicket(conversation)"
                >
                  <div
                    class="text-[12.5px] font-semibold tabular-nums text-[#9AA1AC]"
                  >
                    #{{ conversation.display_id }}
                  </div>
                  <div class="min-w-0">
                    <div
                      class="truncate text-[14px] font-semibold leading-[1.5] text-[#1B1F27] dark:text-white"
                    >
                      {{ conversation.subject }}
                    </div>
                    <div
                      v-if="conversation.category"
                      class="mt-0.5 truncate text-[12px] leading-[1.5] text-[#9AA1AC]"
                    >
                      {{ conversation.category }}
                    </div>
                  </div>
                  <div
                    v-if="showRequester"
                    class="truncate text-[13px] text-[#454B57] dark:text-slate-300"
                  >
                    {{ conversation.requester_name || '—' }}
                  </div>
                  <div>
                    <span
                      class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold"
                      :class="statusStyle(conversation.status).badge"
                    >
                      <span
                        class="h-1.5 w-1.5 shrink-0 rounded-full"
                        :style="{
                          backgroundColor: statusStyle(conversation.status).dot,
                        }"
                      />
                      {{ conversation.status_label }}
                    </span>
                  </div>
                  <div
                    v-if="showAssignee"
                    class="truncate text-[13px] text-[#454B57] dark:text-slate-300"
                  >
                    {{ conversation.assignee_name || '—' }}
                  </div>
                  <div class="text-[12.5px] text-[#6B7280]">
                    {{ conversation.updated_label }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="conversations.length"
            class="mt-[18px] flex flex-col gap-2.5 md:hidden"
          >
            <button
              v-for="conversation in conversations"
              :key="conversation.display_id"
              type="button"
              class="flex w-full flex-col gap-2 rounded-[14px] border border-solid border-[#E4E6EB] bg-white p-4 text-left dark:border-slate-800 dark:bg-slate-900"
              @click="openTicket(conversation)"
            >
              <div class="flex items-center justify-between gap-2">
                <span
                  class="text-[12px] font-semibold tabular-nums text-[#9AA1AC]"
                >
                  #{{ conversation.display_id }}
                </span>
                <span
                  class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold"
                  :class="statusStyle(conversation.status).badge"
                >
                  <span
                    class="h-1.5 w-1.5 shrink-0 rounded-full"
                    :style="{
                      backgroundColor: statusStyle(conversation.status).dot,
                    }"
                  />
                  {{ conversation.status_label }}
                </span>
              </div>
              <div class="min-w-0">
                <div
                  class="truncate text-[14px] font-semibold text-[#1B1F27] dark:text-white"
                >
                  {{ conversation.subject }}
                </div>
                <div
                  v-if="conversation.category"
                  class="mt-0.5 truncate text-[12px] text-[#9AA1AC]"
                >
                  {{ conversation.category }}
                </div>
              </div>
              <div
                class="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[#6B7280]"
              >
                <span v-if="showRequester">
                  {{ labels.columns.requester }}:
                  {{ conversation.requester_name || '—' }}
                </span>
                <span v-if="showAssignee">
                  {{ labels.columns.assignee }}:
                  {{ conversation.assignee_name || '—' }}
                </span>
                <span>{{ conversation.updated_label }}</span>
              </div>
            </button>
          </div>

          <div
            v-if="!conversations.length"
            class="mt-[18px] rounded-[14px] border border-dashed border-[#E7E9ED] px-5 py-14 text-center dark:border-slate-800"
          >
            <div class="text-sm font-bold text-[#1B1F27] dark:text-slate-100">
              {{ labels.empty.title }}
            </div>
            <div class="mt-1 text-[13px] text-[#9AA1AC]">
              {{ labels.empty.subtitle }}
            </div>
          </div>

          <div
            v-if="conversations.length"
            class="mt-3.5 text-[12.5px] text-[#9AA1AC]"
          >
            {{ countLabel }}
          </div>
        </template>

        <div v-if="pagination.total_count" class="mt-6 pb-16 sm:pb-0">
          <PaginationFooter
            :current-page="pagination.current_page"
            :total-items="pagination.total_count"
            :items-per-page="pagination.per_page"
            @update:current-page="goToPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>
